<template>
	<div
		class="w-full bg-surface dark:bg-dark-700 rounded-card p-6 border border-primary-100 dark:border-dark-600 transition-colors duration-300">
		<!-- Header -->
		<div
			class="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
			<div>
				<p
					class="text-[11px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-0.5">
					Analytics
				</p>
				<h2
					class="font-display text-[16px] font-bold text-dark-700 dark:text-white">
					{{ selectedMetricLabel }} Trend
				</h2>
			</div>

			<!-- Metric toggle pills -->
			<div class="flex flex-wrap gap-2">
				<button
					v-for="metric in metrics"
					:key="metric.key"
					@click="selectedMetric = metric.key"
					class="px-3.5 py-1.5 rounded-full text-[12px] font-semibold transition-all duration-200"
					:class="
						selectedMetric === metric.key
							? 'bg-primary-400 text-white shadow-teal'
							: 'bg-primary-50 dark:bg-dark-600 text-muted dark:text-muted-light hover:bg-primary-100 dark:hover:bg-dark-500'
					">
					{{ metric.label }}
				</button>
			</div>
		</div>

		<!-- Empty state -->
		<div
			v-if="!hasChartData"
			class="h-64 flex flex-col items-center justify-center gap-3">
			<div
				class="w-12 h-12 rounded-xl bg-primary-50 dark:bg-dark-600 flex items-center justify-center">
				<svg
					class="w-6 h-6 text-muted dark:text-muted-light"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
				</svg>
			</div>
			<p class="text-[13px] text-muted dark:text-muted-light">
				No data available for
				<span class="font-semibold">{{ selectedMetricLabel }}</span
				>.
			</p>
		</div>

		<!-- Chart -->
		<div v-if="hasChartData" class="h-64">
			<apexchart
				height="256"
				type="bar"
				:options="chartOptions"
				:series="metricSeries" />
		</div>
	</div>
</template>

<script>
import { computed, ref } from "vue";

const metrics = [
	{ key: "sales_velocity", label: "Sales", unit: "tickets" },
	{ key: "revenue", label: "Revenue", unit: "€" },
	{ key: "attendees_created", label: "Attendees", unit: "attendees" },
];

export default {
	name: "StatisticView",
	props: {
		chartData: { type: Array, required: true, default: () => [] },
		timeRange: {
			type: String,
			default: "month",
			validator: (v) => ["day", "month", "year"].includes(v),
		},
	},

	setup(props) {
		const selectedMetric = ref("sales_velocity");

		const formatTimestamp = (date) => {
			const d = new Date(date);
			if (props.timeRange === "day")
				return d.toLocaleDateString("en-US", { weekday: "short" });
			if (props.timeRange === "month")
				return d.toLocaleDateString("en-US", { month: "short" });
			if (props.timeRange === "year") return d.getFullYear().toString();
			return "";
		};

		const groupData = (data) => {
			const map = {};
			data.forEach((item) => {
				const key = formatTimestamp(item.timestamp);
				map[key] = (map[key] || 0) + item.value;
			});
			return Object.entries(map).map(([label, value]) => ({ label, value }));
		};

		const metricSeries = computed(() => {
			const selectedChart = props.chartData.find(
				(c) =>
					c.metric === selectedMetric.value ||
					c.meta?.metric === selectedMetric.value,
			);
			const rawData = selectedChart?.data ?? selectedChart?.meta?.data ?? [];
			if (!rawData.length) return [{ name: selectedMetric.value, data: [] }];
			const grouped = groupData(rawData);
			return [
				{ name: selectedMetric.value, data: grouped.map((d) => d.value) },
			];
		});

		const hasChartData = computed(
			() => metricSeries.value?.[0]?.data?.length > 0,
		);

		const selectedMetricLabel = computed(
			() => metrics.find((m) => m.key === selectedMetric.value)?.label || "",
		);

		const xCategories = computed(() => {
			const selectedChart = props.chartData.find(
				(c) =>
					c.metric === selectedMetric.value ||
					c.meta?.metric === selectedMetric.value,
			);
			const rawData = Array.isArray(selectedChart?.data)
				? selectedChart.data
				: Array.isArray(selectedChart?.meta?.data)
				? selectedChart.meta.data
				: [];
			return groupData(rawData).map((d) => d.label);
		});

		const chartOptions = computed(() => ({
			chart: {
				type: "bar",
				toolbar: { show: false },
				zoom: { enabled: false },
				background: "transparent",
				fontFamily: "Montserrat, sans-serif",
			},
			plotOptions: {
				bar: {
					borderRadius: 8,
					columnWidth: "45%",
				},
			},
			fill: {
				type: "gradient",
				gradient: {
					type: "vertical",
					shadeIntensity: 0.3,
					gradientToColors: ["#0d6b5e"],
					stops: [0, 100],
				},
				colors: ["#1a9b8a"],
			},
			dataLabels: { enabled: false },
			grid: {
				borderColor: "rgba(208, 237, 232, 0.5)",
				strokeDashArray: 4,
				xaxis: { lines: { show: false } },
			},
			xaxis: {
				categories: xCategories.value,
				axisBorder: { show: false },
				axisTicks: { show: false },
				labels: {
					style: {
						colors: "#6b8f89",
						fontSize: "11px",
						fontWeight: 600,
						fontFamily: "Montserrat, sans-serif",
					},
				},
			},
			yaxis: {
				labels: {
					style: {
						colors: "#6b8f89",
						fontSize: "11px",
						fontFamily: "Montserrat, sans-serif",
					},
					formatter: (val) => val.toLocaleString(),
				},
			},
			tooltip: {
				theme: "light",
				style: { fontFamily: "Montserrat, sans-serif" },
			},
			states: {
				hover: { filter: { type: "lighten", value: 0.1 } },
			},
		}));

		return {
			metrics,
			chartOptions,
			selectedMetric,
			metricSeries,
			hasChartData,
			selectedMetricLabel,
		};
	},
};
</script>
